import { ImageScaleView, ImageView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import PopupMatch2BaseFullscreen from '../../minigame/match2-timestep/components/PopupMatch2BaseFullscreen';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { getStreakBattleRoyalePlayersAtMilestone } from '../streakbattleroyale.utils';
import { Avatar } from 'src/game/components/shared/Avatar';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { animateGems } from 'src/game/animation/CurrencyAnimation';
import StreakGlare from '../components/StreakGlare';
import {
  getStreakBattleRoyaleGoal,
  getStreakBattleRoyaleRewardGemsPool,
} from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import StreakTapToContinue from '../components/StreakTapToContinue';
import { streakBattleRoyaleTheme } from '../streakbattleroyale.theme';
import { getPlayerProfileAvatarFrame } from 'src/replicant/profile/profile.getters';

export default class PopupStreakBattleRoyaleRewardV2 extends PopupMatch2BaseFullscreen {
  private banner: ImageView;
  private glare: StreakGlare;

  constructor(opts: { superview: View; close: () => void }) {
    const state = user();
    const goal = getStreakBattleRoyaleGoal(state);
    const players = getStreakBattleRoyalePlayersAtMilestone(goal);
    const pool = getStreakBattleRoyaleRewardGemsPool(state);
    const rewardAmount = Math.round(pool / players.length);

    super({
      superview: opts.superview,
      zIndex: 10000,
      tracking: {
        feature: Feature.MATCH2.STREAK_EVENT._,
        $subFeature: Feature.MATCH2.STREAK_EVENT.REWARD_POPUP,
      },
      bgColor: 'rgba(70,29,84,0.9)',
    });

    const { width, height } = this.getView().style;
    const locale = StateObserver.getLocale();

    // title
    new ImageScaleView({
      superview: this.panel,
      image: streakBattleRoyaleTheme().getAsset(
        `title_congratulations_${locale}`,
      ),
      scaleMethod: 'contain',
      width: 540,
      height: 83,
      x: width * 0.5,
      y: 170,
      centerOnOrigin: true,
    });

    const rewardView = new View({
      superview: this.panel,
      x: width * 0.5,
      y: height * (players.length > 1 ? 0.4 : 0.46),
    });

    // shine glare
    this.glare = new StreakGlare({
      superview: rewardView,
      opacity: 0.3,
    });

    // reward label
    new LangBitmapFontTextShadowView({
      superview: rewardView,
      font: 'Title',
      localeText: () =>
        i18n('match2.streakBattleRoyale.congratulations.message1V2'),
      size: 32,
      width,
      height: 58,
      align: 'center',
      verticalAlign: 'center',
      color: '#FEF3B6',
      textShadowOpacity: 0.25,
      textShadowOffsetX: 0,
      textShadowOffsetY: 6,
      centerOnOrigin: true,
      y: -147,
    });

    // gems pile
    new ImageView({
      superview: rewardView,
      image: streakBattleRoyaleTheme().getAsset('trophy'),
      width: 416,
      height: 280,
      x: 10,
      centerOnOrigin: true,
    });

    // player avatar
    new Avatar({
      superview: rewardView,
      width: 90,
      height: 90,
      y: -45,
      borderTint: '#FFF503',
      dropShadow: true,
      frame: getPlayerProfileAvatarFrame(user()),
    }).updateImage(players[0].photo ?? '');

    this.banner = new ImageView({
      superview: rewardView,
      image: streakBattleRoyaleTheme().getAsset('gem_banner'),
      width: 173,
      height: 58,
      centerOnOrigin: true,
      y: 45,
    });

    // gems label
    new LangBitmapFontTextShadowView({
      superview: this.banner,
      font: 'Body',
      localeText: () => `${rewardAmount}`,
      size: 29,
      width: 150,
      height: 60,
      align: 'center',
      verticalAlign: 'center',
      color: '#FFFFFF',
      textShadowOpacity: 0.8,
      textShadowOffsetX: 0,
      textShadowOffsetY: 6,
      centerOnOrigin: true,
      x: this.banner.style.width * 0.5 + 17,
      y: this.banner.style.height * 0.5 - 6,
    });

    if (players.length > 1) {
      const max = 9;
      const playerCount = Math.min(players.length, max + 1);
      const y = height - (playerCount > 6 ? 545 : 450);

      // message
      new LangBitmapFontTextShadowView({
        superview: this.panel,
        font: 'Body',
        localeText: () =>
          i18n('match2.streakBattleRoyale.congratulations.message2', {
            prize: getStreakBattleRoyaleRewardGemsPool(user()),
            winners: players.length,
          }),
        size: 26,
        width,
        height: 111,
        align: 'center',
        verticalAlign: 'center',
        color: '#FFFFFF',
        textShadowOpacity: 0.8,
        textShadowOffsetX: 0,
        textShadowOffsetY: 5,
        y,
      });

      // opponent avatars
      const wrapperSize = 95;
      const avatarSize = 82;
      const opponentView = new FlexBoxView({
        superview: this.panel,
        width: 490,
        centerOnOrigin: true,
        x: width * 0.5,
        y: y + 115,
        flex: {
          justifyContent: 'center',
          flexWrap: 'wrap',
        },
      });

      // skip self
      for (let index = 1; index < playerCount; index++) {
        const { photo } = players[index];
        const wrapper = new View({
          superview: opponentView,
          width: wrapperSize,
          height: wrapperSize,
        });
        const avatar = new Avatar({
          superview: wrapper,
          width: avatarSize,
          height: avatarSize,
          x: wrapperSize * 0.5,
          y: wrapperSize * 0.5,
          dropShadow: true,
        });
        avatar.updateImage(photo ?? '');
      }
    }

    // 'Tap to continue' CTA
    new StreakTapToContinue({
      superview: this.panel,
      offsetY: -35,
      textOpts: {
        color: '#FFE380',
        textShadowOpacity: 1,
        textShadowColor: '#613428',
        textShadowOffsetY: 8,
      },
      tracking: {
        feature: Feature.MATCH2.STREAK_EVENT._,
        $subFeature: Feature.MATCH2.STREAK_EVENT.MATCHMAKING_POPUP_CONTINUE,
      },
      onClick: async () => {
        await StateObserver.invoke.grantStreakBattleRoyaleReward();

        const superview = this.banner.getSuperview()!.getSuperview()!;
        const position = this.banner.getPosition(superview);

        position.x += 36;
        position.y += this.banner.style.height * 0.5 - 2;

        await animateGems({
          superview,
          start: { position, scale: 0.5 },
          count: rewardAmount,
        });

        opts.close();
      },
    });
  }

  public override async fadeOut() {
    await super.fadeOut();
    this.glare.stopAnimation();
  }
}
